import axios from 'axios';
import { API_BASE_URL } from './config';
import { normalizeApiNextUrl } from './utils/normalizeApiNextUrl';

// Base axios instance; token/header injection handled elsewhere (e.g., App authAxios)
const api = axios.create({
  baseURL: API_BASE_URL,
});

// --- Generic helpers ---

// Walks every DRF page via `next` and returns the full concatenated
// `results` array. Several one-off fetch helpers below used to read only
// page 1 (CustomPagination defaults to page_size=10), which silently
// truncated any list past its first 10 records — this mirrors the same
// walk-every-page pattern App.js's `createFetchFunction` already uses for
// Clients/Cases/Invoices/etc., so every list in the app now loads
// completely before it's paginated client-side.
export const fetchAllPages = async (authAxios, url, params = {}) => {
  const first = await authAxios.get(url, { params });
  const firstData = first?.data;
  if (Array.isArray(firstData)) return firstData;
  if (!firstData || !Array.isArray(firstData.results)) return [];

  let all = [...firstData.results];
  let nextUrl = firstData.next ? normalizeApiNextUrl(firstData.next) : null;
  while (nextUrl) {
    const resp = await authAxios.get(nextUrl);
    const data = resp?.data;
    if (!data || !Array.isArray(data.results)) break;
    all = all.concat(data.results);
    nextUrl = data.next ? normalizeApiNextUrl(data.next) : null;
  }
  return all;
};
export const fetchInvoice = async (authAxios, id) => {
  const resp = await authAxios.get(`invoices/${id}/`);
  return resp.data;
};

export const patchInvoiceItems = async (authAxios, id, items) => {
  const payload = { items };
  const resp = await authAxios.patch(`invoices/${id}/`, payload);
  return resp.data;
};

export const createInvoiceJournal = async (authAxios, invoiceId, journal_type, amount, notes='') => {
  const payload = { invoice: invoiceId, journal_type, amount, notes };
  const resp = await authAxios.post('invoice-journals/', payload);
  return resp.data;
};

export const listInvoiceJournals = async (authAxios, invoiceId) => {
  const resp = await authAxios.get('invoice-journals/', { params: { invoice: invoiceId } });
  return resp.data?.results || resp.data; // support paginated or flat
};

// --- Collections: settings and templates ---
export const getCollectionsSettings = async (authAxios) => {
  const resp = await authAxios.get('collections/settings/');
  return resp.data;
};

export const updateCollectionsSettings = async (authAxios, payload) => {
  const resp = await authAxios.put('collections/settings/', payload);
  return resp.data;
};

// --- Firm Tax Settings ---
export const getFirmTaxSettings = async (authAxios) => {
  const resp = await authAxios.get('firm/settings/');
  return resp.data;
};
export const updateFirmTaxSettings = async (authAxios, payload) => {
  const resp = await authAxios.patch('firm/settings/', payload);
  return resp.data;
};
export const testMsGraphConnection = async (authAxios) => {
  const resp = await authAxios.post('firm/settings/');
  return resp.data;
};

// --- Module Access Control (Partner Controls screen) ---
export const fetchModuleAccess = async (authAxios) => {
  const resp = await authAxios.get('firm/module-access/');
  return resp.data;
};
export const updateModuleAccess = async (authAxios, { module_key, role, disabled }) => {
  const resp = await authAxios.patch('firm/module-access/', { module_key, role, disabled });
  return resp.data;
};

export const listEmailTemplates = async (authAxios, params = {}) => {
  const resp = await authAxios.get('email-templates/', { params });
  return resp.data?.results || resp.data;
};

export const createEmailTemplate = async (authAxios, data) => {
  const resp = await authAxios.post('email-templates/', data);
  return resp.data;
};

export const updateEmailTemplate = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`email-templates/${id}/`, data);
  return resp.data;
};

export const deleteEmailTemplate = async (authAxios, id) => {
  await authAxios.delete(`email-templates/${id}/`);
};

// --- Invoice-level collections actions ---
export const collectionsPreviewInvoice = async (authAxios, invoiceId) => {
  const resp = await authAxios.get(`invoices/${invoiceId}/collections/preview/`);
  return resp.data;
};

export const collectionsSendInvoice = async (authAxios, invoiceId) => {
  const resp = await authAxios.post(`invoices/${invoiceId}/collections/send/`);
  return resp.data;
};

export const collectionsAdvanceInvoice = async (authAxios, invoiceId, sendNow=false) => {
  const resp = await authAxios.post(`invoices/${invoiceId}/collections/advance/`, { send_now: !!sendNow });
  return resp.data;
};

// --- Dunning communication logs ---
export const listDunningLogsForInvoice = async (authAxios, invoiceId, limit=20) => {
  const resp = await authAxios.get('communication-logs/', { params: { invoice: invoiceId, collections_only: 'true' } });
  const data = resp.data?.results || resp.data;
  return Array.isArray(data) ? data.slice(0, limit) : [];
};

// --- WIP (Work In Progress) invoicing helpers ---
export const listTimeEntries = async (authAxios, params={}) => {
  const resp = await authAxios.get('time-entries/', { params });
  return resp.data?.results || resp.data;
};

export const listExpenseEntries = async (authAxios, params={}) => {
  const resp = await authAxios.get('expense-entries/', { params });
  return resp.data?.results || resp.data;
};

export const listClients = async (authAxios, params={}) => {
  const resp = await authAxios.get('clients/', { params });
  return resp.data?.results || resp.data;
};

export const listCases = async (authAxios, params={}) => {
  const resp = await authAxios.get('cases/', { params });
  return resp.data?.results || resp.data;
};

export const generateInvoiceFromWip = async (authAxios, payload) => {
  // payload: { client?, case?, time_entry_ids?, expense_entry_ids? }
  const resp = await authAxios.post('invoices/generate_from_wip/', payload);
  return resp.data;
};

export const fetchInvoicePaymentTotals = async (authAxios) => {
  const resp = await authAxios.get('invoices/payment_totals/');
  return resp.data;
};

// --- Case Stages helper ---
export const listStagesForType = async (authAxios, caseType) => {
  const params = caseType ? { case_type: caseType } : {};
  const resp = await authAxios.get('cases/stages-for-type/', { params });
  return resp.data;
};

// --- Task Template helpers ---
export const listTaskTemplates = async (authAxios, params = {}) => {
  const resp = await authAxios.get('task-templates/', { params });
  return resp.data?.results || resp.data;
};

export const createTaskTemplate = async (authAxios, data) => {
  const resp = await authAxios.post('task-templates/', data);
  return resp.data;
};

export const updateTaskTemplate = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`task-templates/${id}/`, data);
  return resp.data;
};

export const deactivateTaskTemplate = async (authAxios, id) => {
  const resp = await authAxios.post(`task-templates/${id}/deactivate/`);
  return resp.data;
};

export const activateTaskTemplate = async (authAxios, id) => {
  const resp = await authAxios.post(`task-templates/${id}/activate/`);
  return resp.data;
};

export const saveStageTaskTemplates = async (authAxios, caseId, overwrite=true) => {
  const resp = await authAxios.post(`cases/${caseId}/save_stage_task_templates/`, { overwrite });
  return resp.data;
};

// =============================================================================
// EMAIL-TO-MATTER LINKING helpers
// =============================================================================

export const fetchLinkedEmails = async (authAxios, caseId, search='') => {
  const params = search ? { search } : {};
  const resp = await authAxios.get(`cases/${caseId}/linked-emails/`, { params });
  return resp.data;
};

export const fileEmailToMatter = async (authAxios, caseId, emailData) => {
  const resp = await authAxios.post(`cases/${caseId}/file-email/`, emailData);
  return resp.data;
};

export const fileEmailAttachments = async (authAxios, caseId, data) => {
  const resp = await authAxios.post(`cases/${caseId}/file-email-attachments/`, data);
  return resp.data;
};

// =============================================================================
// ACCOUNTING MODULE – Phase 1 API helpers
// =============================================================================

// --- Chart of Accounts (GL Accounts) ---
export const listGLAccounts = async (authAxios, params={}) => {
  const resp = await authAxios.get('gl-accounts/', { params });
  return resp.data?.results || resp.data;
};
export const createGLAccount = async (authAxios, data) => {
  const resp = await authAxios.post('gl-accounts/', data);
  return resp.data;
};
export const updateGLAccount = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`gl-accounts/${id}/`, data);
  return resp.data;
};
export const deleteGLAccount = async (authAxios, id) => {
  await authAxios.delete(`gl-accounts/${id}/`);
};
export const seedGLAccounts = async (authAxios) => {
  const resp = await authAxios.post('gl-accounts/seed/');
  return resp.data;
};
export const fetchGLAccountTransactions = async (authAxios, accountId, params={}) => {
  const resp = await authAxios.get(`gl-accounts/${accountId}/transactions/`, { params });
  return resp.data;
};

// --- Journal Entries ---
export const listJournalEntries = async (authAxios, params={}) => {
  // Was a single-page GET (CustomPagination defaults to page_size=10),
  // silently truncating General Ledger to its 10 most recent entries and,
  // via the same function, truncating the "match to journal entry" picker
  // in Bank Reconciliation the same way. fetchAllPages walks every page.
  return fetchAllPages(authAxios, 'gl-journal-entries/', params);
};
export const createManualJournal = async (authAxios, data) => {
  const resp = await authAxios.post('gl-journal-entries/create-manual/', data);
  return resp.data;
};
export const reverseJournalEntry = async (authAxios, id, reason='') => {
  const resp = await authAxios.post(`gl-journal-entries/${id}/reverse/`, { reason });
  return resp.data;
};

// --- Suppliers ---
export const listSuppliers = async (authAxios, params={}) => {
  const resp = await authAxios.get('suppliers/', { params });
  return resp.data?.results || resp.data;
};
export const createSupplier = async (authAxios, data) => {
  const resp = await authAxios.post('suppliers/', data);
  return resp.data;
};
export const updateSupplier = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`suppliers/${id}/`, data);
  return resp.data;
};
export const deleteSupplier = async (authAxios, id) => {
  await authAxios.delete(`suppliers/${id}/`);
};

// --- Quick Memo (personal scratchpad) ---
export const fetchQuickMemos = async (authAxios) => {
  const resp = await authAxios.get('quick-memos/');
  return resp.data;
};
export const createQuickMemo = async (authAxios, data) => {
  const resp = await authAxios.post('quick-memos/', data);
  return resp.data;
};
export const updateQuickMemo = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`quick-memos/${id}/`, data);
  return resp.data;
};
export const deleteQuickMemo = async (authAxios, id) => {
  await authAxios.delete(`quick-memos/${id}/`);
};

// --- AP Bills ---
export const listAPBills = async (authAxios, params={}) => {
  const resp = await authAxios.get('ap-bills/', { params });
  return resp.data?.results || resp.data;
};
export const fetchAPBill = async (authAxios, id) => {
  const resp = await authAxios.get(`ap-bills/${id}/`);
  return resp.data;
};
export const createAPBill = async (authAxios, data) => {
  // Support FormData for receipt file uploads
  if (data instanceof FormData) {
    const resp = await authAxios.post('ap-bills/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return resp.data;
  }
  // Send items as items_input for the nested serializer
  const payload = { ...data };
  if (payload.items && !payload.items_input) {
    payload.items_input = payload.items;
    delete payload.items;
  }
  const resp = await authAxios.post('ap-bills/', payload);
  return resp.data;
};
export const updateAPBill = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`ap-bills/${id}/`, data);
  return resp.data;
};
export const approveAPBill = async (authAxios, id) => {
  const resp = await authAxios.post(`ap-bills/${id}/approve/`);
  return resp.data;
};
export const recordAPBillPayment = async (authAxios, id, data) => {
  const resp = await authAxios.post(`ap-bills/${id}/record-payment/`, data);
  return resp.data;
};
export const batchPayAPBills = async (authAxios, data) => {
  const resp = await authAxios.post('ap-bills/batch-pay/', data);
  return resp.data;
};

// --- AP Payments (read-only) ---
export const listAPPayments = async (authAxios, params={}) => {
  const resp = await authAxios.get('ap-payments/', { params });
  return resp.data?.results || resp.data;
};

// --- Petty Cash ---
export const listPettyCashFunds = async (authAxios, params={}) => {
  const resp = await authAxios.get('petty-cash-funds/', { params });
  return resp.data?.results || resp.data;
};
export const createPettyCashFund = async (authAxios, data) => {
  const resp = await authAxios.post('petty-cash-funds/', data);
  return resp.data;
};
export const updatePettyCashFund = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`petty-cash-funds/${id}/`, data);
  return resp.data;
};
export const replenishPettyCashFund = async (authAxios, id, data) => {
  const resp = await authAxios.post(`petty-cash-funds/${id}/replenish/`, typeof data === 'object' ? data : { amount: data });
  return resp.data;
};
export const listPettyCashTransactions = async (authAxios, params={}) => {
  const resp = await authAxios.get('petty-cash-transactions/', { params });
  return resp.data?.results || resp.data;
};
export const createPettyCashTransaction = async (authAxios, data) => {
  // Support FormData for receipt file uploads
  if (data instanceof FormData) {
    const resp = await authAxios.post('petty-cash-transactions/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return resp.data;
  }
  const resp = await authAxios.post('petty-cash-transactions/', data);
  return resp.data;
};
export const getPettyCashSummary = async (authAxios, params={}) => {
  const resp = await authAxios.get('petty-cash-transactions/summary/', { params });
  return resp.data;
};
export const approvePettyCashTransaction = async (authAxios, id) => {
  const resp = await authAxios.post(`petty-cash-transactions/${id}/approve/`);
  return resp.data;
};
export const rejectPettyCashTransaction = async (authAxios, id) => {
  const resp = await authAxios.post(`petty-cash-transactions/${id}/reject/`);
  return resp.data;
};
export const voidPettyCashTransaction = async (authAxios, id, reason) => {
  const resp = await authAxios.post(`petty-cash-transactions/${id}/void/`, { reason });
  return resp.data;
};
export const getPettyCashTransaction = async (authAxios, id) => {
  const resp = await authAxios.get(`petty-cash-transactions/${id}/`);
  return resp.data;
};
export const exportPettyCashReport = async (authAxios, fundId, params={}) => {
  const resp = await authAxios.get(`petty-cash-funds/${fundId}/export-report/`, {
    params,
    responseType: 'blob',
  });
  return resp.data;
};

// --- Accounting Review ---
export const getAccountingReview = async (authAxios) => {
  const resp = await authAxios.get('accounting-review/');
  return resp.data;
};
export const flagJournalEntry = async (authAxios, id, data) => {
  const resp = await authAxios.post(`gl-journal-entries/${id}/flag/`, data);
  return resp.data;
};

// --- Dual-approval workflow (paralegal-created cases/time/expense entries) ---
export const fetchPendingApprovalCases = async (authAxios) => {
  const resp = await authAxios.get('cases/', { params: { approval_status: 'PENDING_APPROVAL', page_size: 200 } });
  return resp.data?.results || resp.data || [];
};
export const fetchPendingApprovalTimeEntries = async (authAxios) => {
  const resp = await authAxios.get('time-entries/', { params: { approval_status: 'PENDING_APPROVAL', page_size: 200 } });
  return resp.data?.results || resp.data || [];
};
export const fetchPendingApprovalExpenseEntries = async (authAxios) => {
  const resp = await authAxios.get('expense-entries/', { params: { approval_status: 'PENDING_APPROVAL', page_size: 200 } });
  return resp.data?.results || resp.data || [];
};
export const approveCase = async (authAxios, id) => {
  const resp = await authAxios.post(`cases/${id}/approve/`);
  return resp.data;
};
export const rejectCase = async (authAxios, id, reason) => {
  const resp = await authAxios.post(`cases/${id}/reject/`, { reason });
  return resp.data;
};
export const approveTimeEntry = async (authAxios, id) => {
  const resp = await authAxios.post(`time-entries/${id}/approve/`);
  return resp.data;
};
export const rejectTimeEntry = async (authAxios, id, reason) => {
  const resp = await authAxios.post(`time-entries/${id}/reject/`, { reason });
  return resp.data;
};
export const approveExpenseEntry = async (authAxios, id) => {
  const resp = await authAxios.post(`expense-entries/${id}/approve/`);
  return resp.data;
};
export const rejectExpenseEntry = async (authAxios, id, reason) => {
  const resp = await authAxios.post(`expense-entries/${id}/reject/`, { reason });
  return resp.data;
};

// --- Bank Account Opening Balance ---
export const updateBankOpeningBalance = async (authAxios, id, data) => {
  const resp = await authAxios.post(`bank-accounts/${id}/update-opening-balance/`, data);
  return resp.data;
};
export const getBankOpeningBalanceLogs = async (authAxios, id) => {
  const resp = await authAxios.get(`bank-accounts/${id}/opening-balance-logs/`);
  return resp.data;
};

// --- Firm Expenses ---
export const listFirmExpenses = async (authAxios, params={}) => {
  const resp = await authAxios.get('firm-expenses/', { params });
  return resp.data?.results || resp.data;
};
export const createFirmExpense = async (authAxios, data) => {
  const resp = await authAxios.post('firm-expenses/', data);
  return resp.data;
};
export const updateFirmExpense = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`firm-expenses/${id}/`, data);
  return resp.data;
};
export const approveFirmExpense = async (authAxios, id) => {
  const resp = await authAxios.post(`firm-expenses/${id}/approve/`);
  return resp.data;
};
export const payFirmExpense = async (authAxios, id, data) => {
  const resp = await authAxios.post(`firm-expenses/${id}/pay/`, data);
  return resp.data;
};
export const cancelRecurringExpense = async (authAxios, id) => {
  const resp = await authAxios.post(`firm-expenses/${id}/cancel-recurring/`);
  return resp.data;
};
export const rejectFirmExpense = async (authAxios, id) => {
  const resp = await authAxios.post(`firm-expenses/${id}/reject/`);
  return resp.data;
};
export const getFirmExpense = async (authAxios, id) => {
  const resp = await authAxios.get(`firm-expenses/${id}/`);
  return resp.data;
};

// --- Accounting Dashboard Charts ---
export const fetchAccountingCharts = async (authAxios, params={}) => {
  const resp = await authAxios.get('reports/accounting-charts/', { params });
  return resp.data;
};
export const fetchAccountingDashboardPdf = async (authAxios, params={}) => {
  const resp = await authAxios.get('reports/accounting-charts/pdf/', { params, responseType: 'blob' });
  return resp.data;
};

// --- Accounting Reports ---
export const fetchTrialBalance = async (authAxios, params={}) => {
  const resp = await authAxios.get('reports/trial-balance/', { params });
  return resp.data;
};
export const fetchIncomeStatement = async (authAxios, params={}) => {
  const resp = await authAxios.get('reports/income-statement/', { params });
  return resp.data;
};
export const fetchCreditorAging = async (authAxios) => {
  const resp = await authAxios.get('reports/creditor-aging/');
  return resp.data;
};
export const fetchBalanceSheet = async (authAxios, params={}) => {
  const resp = await authAxios.get('reports/balance-sheet/', { params });
  return resp.data;
};
export const fetchTrustReconciliation = async (authAxios, params={}) => {
  const resp = await authAxios.get('reports/trust-reconciliation/', { params });
  return resp.data;
};

// --- Trust Accounting helpers ---
export const reverseTrustTransaction = async (authAxios, transactionId, reason) => {
  const resp = await authAxios.post(`trust-transactions/${transactionId}/reverse/`, { reason });
  return resp.data;
};

export const exportTrustLedgerPDF = async (authAxios, accountId, params={}) => {
  const resp = await authAxios.get(`trust-accounts/${accountId}/export-ledger-pdf/`, {
    params,
    responseType: 'blob',
  });
  return resp.data;
};

export const fetchAuditTrail = async (authAxios, params={}) => {
  const resp = await authAxios.get('reports/audit-trail/', { params });
  return resp.data;
};
export const downloadAuditTrailCSV = async (authAxios, params={}) => {
  const resp = await authAxios.get('reports/audit-trail/', { params: { ...params, export_format: 'csv' }, responseType: 'blob' });
  return resp.data;
};
export const autoMatchBankLines = async (authAxios, statementId) => {
  const resp = await authAxios.post('bank-statement-lines/auto-match/', { statement_id: statementId });
  return resp.data;
};

// =============================================================================
// Phase 2: Bank Accounts, Bank Reconciliation & VAT Returns
// =============================================================================

// --- Bank Accounts ---
export const listBankAccounts = async (authAxios, params={}) => {
  const resp = await authAxios.get('bank-accounts/', { params });
  return resp.data?.results || resp.data;
};
export const createBankAccount = async (authAxios, data) => {
  const resp = await authAxios.post('bank-accounts/', data);
  return resp.data;
};
export const updateBankAccount = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`bank-accounts/${id}/`, data);
  return resp.data;
};
export const deleteBankAccount = async (authAxios, id) => {
  await authAxios.delete(`bank-accounts/${id}/`);
};

// --- Bank Statements ---
export const listBankStatements = async (authAxios, params={}) => {
  const resp = await authAxios.get('bank-statements/', { params });
  return resp.data?.results || resp.data;
};
export const createBankStatement = async (authAxios, data) => {
  const resp = await authAxios.post('bank-statements/', data);
  return resp.data;
};
export const updateBankStatement = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`bank-statements/${id}/`, data);
  return resp.data;
};
export const fetchBankStatement = async (authAxios, id) => {
  const resp = await authAxios.get(`bank-statements/${id}/`);
  return resp.data;
};
export const importBankStatementCSV = async (authAxios, id, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const resp = await authAxios.post(`bank-statements/${id}/import-csv/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return resp.data;
};

// Bank Statement Importer (CSV/Excel + auto-aligning column-mapping wizard).
// Closes QA Trust #4 (2026-05-29 bug report). Stage 1 = preview, Stage 2 = commit.
export const previewBankImport = async (authAxios, { file, bankAccountId }) => {
  const formData = new FormData();
  formData.append('file', file);
  if (bankAccountId) formData.append('bank_account_id', bankAccountId);
  const resp = await authAxios.post('bank-import/preview/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return resp.data;
};

export const commitBankImport = async (authAxios, { file, bankAccountId, columnMap, periodStart, periodEnd, templateName }) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('bank_account_id', bankAccountId);
  formData.append('column_map', JSON.stringify(columnMap));
  if (periodStart) formData.append('period_start', periodStart);
  if (periodEnd) formData.append('period_end', periodEnd);
  if (templateName) formData.append('template_name', templateName);
  const resp = await authAxios.post('bank-import/commit/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return resp.data;
};

// --- Bank Statement Lines ---
export const listBankStatementLines = async (authAxios, params={}) => {
  // Same page-1-only truncation bug as listJournalEntries above — a
  // manually-transcribed paper statement with more than 10 lines was
  // silently cut off. fetchAllPages walks every page.
  return fetchAllPages(authAxios, 'bank-statement-lines/', params);
};
export const createBankStatementLine = async (authAxios, data) => {
  const resp = await authAxios.post('bank-statement-lines/', data);
  return resp.data;
};
export const updateBankStatementLine = async (authAxios, lineId, data) => {
  const resp = await authAxios.patch(`bank-statement-lines/${lineId}/`, data);
  return resp.data;
};
export const deleteBankStatementLine = async (authAxios, lineId) => {
  await authAxios.delete(`bank-statement-lines/${lineId}/`);
};
export const matchBankStatementLine = async (authAxios, lineId, journalEntryId) => {
  const resp = await authAxios.post(`bank-statement-lines/${lineId}/match/`, { journal_entry_id: journalEntryId });
  return resp.data;
};
export const unmatchBankStatementLine = async (authAxios, lineId) => {
  const resp = await authAxios.post(`bank-statement-lines/${lineId}/unmatch/`);
  return resp.data;
};

// --- Bank Reconciliations ---
export const listBankReconciliations = async (authAxios, params={}) => {
  const resp = await authAxios.get('bank-reconciliations/', { params });
  return resp.data?.results || resp.data;
};
export const createBankReconciliation = async (authAxios, data) => {
  const resp = await authAxios.post('bank-reconciliations/', data);
  return resp.data;
};
export const fetchBankReconciliation = async (authAxios, id) => {
  const resp = await authAxios.get(`bank-reconciliations/${id}/`);
  return resp.data;
};
export const completeBankReconciliation = async (authAxios, id) => {
  const resp = await authAxios.post(`bank-reconciliations/${id}/complete/`);
  return resp.data;
};

// --- VAT Returns ---
export const listVATReturns = async (authAxios, params={}) => {
  const resp = await authAxios.get('vat-returns/', { params });
  return resp.data?.results || resp.data;
};
export const createVATReturn = async (authAxios, data) => {
  const resp = await authAxios.post('vat-returns/', data);
  return resp.data;
};
export const fetchVATReturn = async (authAxios, id) => {
  const resp = await authAxios.get(`vat-returns/${id}/`);
  return resp.data;
};
export const calculateVATReturn = async (authAxios, id) => {
  const resp = await authAxios.post(`vat-returns/${id}/calculate/`);
  return resp.data;
};
export const calculateVATReturnFromGL = async (authAxios, id) => {
  const resp = await authAxios.post(`vat-returns/${id}/calculate-from-gl/`);
  return resp.data;
};
export const fileVATReturn = async (authAxios, id, data={}) => {
  const resp = await authAxios.post(`vat-returns/${id}/file/`, data);
  return resp.data;
};
export const exportVATReturnPDF = async (authAxios, id) => {
  const resp = await authAxios.get(`vat-returns/${id}/export-pdf/`, { responseType: 'blob' });
  return resp.data;
};

// ============================================================================
// Phase 4: Fixed Assets, Payroll Integration, Budgeting
// ============================================================================

// --- Fixed Assets ---
export const fetchFixedAssets = async (authAxios, params = {}) => {
  const resp = await authAxios.get('fixed-assets/', { params });
  return resp.data;
};
export const fetchFixedAsset = async (authAxios, id) => {
  const resp = await authAxios.get(`fixed-assets/${id}/`);
  return resp.data;
};
export const createFixedAsset = async (authAxios, data) => {
  const resp = await authAxios.post('fixed-assets/', data);
  return resp.data;
};
export const updateFixedAsset = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`fixed-assets/${id}/`, data);
  return resp.data;
};
export const runDepreciation = async (authAxios, data) => {
  const resp = await authAxios.post('fixed-assets/run-depreciation/', data);
  return resp.data;
};
export const disposeFixedAsset = async (authAxios, id, data) => {
  const resp = await authAxios.post(`fixed-assets/${id}/dispose/`, data);
  return resp.data;
};
export const fetchAssetRegisterReport = async (authAxios) => {
  const resp = await authAxios.get('fixed-assets/register-report/');
  return resp.data;
};
export const fetchDepreciationEntries = async (authAxios, params = {}) => {
  const resp = await authAxios.get('depreciation-entries/', { params });
  return resp.data;
};

// --- Payroll ---
export const fetchPayrollRuns = async (authAxios, params = {}) => {
  const resp = await authAxios.get('payroll-runs/', { params });
  return resp.data;
};
export const fetchPayrollRun = async (authAxios, id) => {
  const resp = await authAxios.get(`payroll-runs/${id}/`);
  return resp.data;
};
export const createPayrollRun = async (authAxios, data) => {
  const resp = await authAxios.post('payroll-runs/', data);
  return resp.data;
};
export const recalculatePayrollRun = async (authAxios, id) => {
  const resp = await authAxios.post(`payroll-runs/${id}/recalculate/`);
  return resp.data;
};
export const approvePayrollRun = async (authAxios, id) => {
  const resp = await authAxios.post(`payroll-runs/${id}/approve/`);
  return resp.data;
};
export const postPayrollRunToGL = async (authAxios, id) => {
  const resp = await authAxios.post(`payroll-runs/${id}/post-to-gl/`);
  return resp.data;
};
export const fetchPayrollLineItems = async (authAxios, params = {}) => {
  const resp = await authAxios.get('payroll-line-items/', { params });
  return resp.data;
};
export const createPayrollLineItem = async (authAxios, data) => {
  const resp = await authAxios.post('payroll-line-items/', data);
  return resp.data;
};
export const updatePayrollLineItem = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`payroll-line-items/${id}/`, data);
  return resp.data;
};
export const deletePayrollLineItem = async (authAxios, id) => {
  const resp = await authAxios.delete(`payroll-line-items/${id}/`);
  return resp.data;
};

// --- Payroll Phase 2: Lock, Reverse, Auto-Generate, Payslips ---
export const autoGeneratePayrollRun = async (authAxios, id) => {
  const resp = await authAxios.post(`payroll-runs/${id}/auto-generate/`);
  return resp.data;
};
export const lockPayrollRun = async (authAxios, id) => {
  const resp = await authAxios.post(`payroll-runs/${id}/lock/`);
  return resp.data;
};
export const reversePayrollRun = async (authAxios, id, data = {}) => {
  const resp = await authAxios.post(`payroll-runs/${id}/reverse/`, data);
  return resp.data;
};
export const downloadPayslips = async (authAxios, id) => {
  const resp = await authAxios.get(`payroll-runs/${id}/payslips/`, {
    responseType: 'blob',
  });
  return resp;
};

// --- Staff Profiles ---
export const fetchStaffProfiles = async (authAxios, params = {}) => {
  const resp = await authAxios.get('staff-profiles/', { params });
  return resp.data;
};
export const fetchStaffProfile = async (authAxios, id) => {
  const resp = await authAxios.get(`staff-profiles/${id}/`);
  return resp.data;
};
export const updateStaffProfile = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`staff-profiles/${id}/`, data);
  return resp.data;
};
export const createStaffProfile = async (authAxios, data) => {
  const resp = await authAxios.post('staff-profiles/', data);
  return resp.data;
};

// --- Legal Clauses ---
export const fetchLegalClauses = async (authAxios, params = {}) => {
  const resp = await authAxios.get('legal-clauses/', { params });
  return resp.data;
};
export const createLegalClause = async (authAxios, data) => {
  const resp = await authAxios.post('legal-clauses/', data);
  return resp.data;
};
export const updateLegalClause = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`legal-clauses/${id}/`, data);
  return resp.data;
};
export const deleteLegalClause = async (authAxios, id) => {
  await authAxios.delete(`legal-clauses/${id}/`);
};

// --- Leave Management ---
export const fetchLeaveBalances = async (authAxios, params = {}) => {
  const resp = await authAxios.get('leave-balances/', { params });
  return resp.data;
};
export const createLeaveBalance = async (authAxios, data) => {
  const resp = await authAxios.post('leave-balances/', data);
  return resp.data;
};
export const updateLeaveBalance = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`leave-balances/${id}/`, data);
  return resp.data;
};
export const fetchLeaveRequests = async (authAxios, params = {}) => {
  const resp = await authAxios.get('leave-requests/', { params });
  return resp.data;
};
export const createLeaveRequest = async (authAxios, data) => {
  const resp = await authAxios.post('leave-requests/', data);
  return resp.data;
};
export const approveLeaveRequest = async (authAxios, id) => {
  const resp = await authAxios.post(`leave-requests/${id}/approve/`);
  return resp.data;
};
export const rejectLeaveRequest = async (authAxios, id, data) => {
  const resp = await authAxios.post(`leave-requests/${id}/reject/`, data);
  return resp.data;
};
export const cancelLeaveRequest = async (authAxios, id) => {
  const resp = await authAxios.post(`leave-requests/${id}/cancel/`);
  return resp.data;
};

// --- Tax Brackets ---
export const fetchTaxBrackets = async (authAxios, params = {}) => {
  const resp = await authAxios.get('tax-brackets/', { params });
  return resp.data;
};
export const createTaxBracket = async (authAxios, data) => {
  const resp = await authAxios.post('tax-brackets/', data);
  return resp.data;
};
export const updateTaxBracket = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`tax-brackets/${id}/`, data);
  return resp.data;
};
export const deleteTaxBracket = async (authAxios, id) => {
  const resp = await authAxios.delete(`tax-brackets/${id}/`);
  return resp.data;
};

// --- Budgeting ---
export const fetchBudgets = async (authAxios, params = {}) => {
  const resp = await authAxios.get('budgets/', { params });
  return resp.data;
};
export const fetchBudget = async (authAxios, id) => {
  const resp = await authAxios.get(`budgets/${id}/`);
  return resp.data;
};
export const createBudget = async (authAxios, data) => {
  const resp = await authAxios.post('budgets/', data);
  return resp.data;
};
export const updateBudget = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`budgets/${id}/`, data);
  return resp.data;
};
export const approveBudget = async (authAxios, id) => {
  const resp = await authAxios.post(`budgets/${id}/approve/`);
  return resp.data;
};
export const fetchBudgetVarianceReport = async (authAxios, id) => {
  const resp = await authAxios.get(`budgets/${id}/variance-report/`);
  return resp.data;
};
export const fetchBudgetLineItems = async (authAxios, params = {}) => {
  const resp = await authAxios.get('budget-line-items/', { params });
  return resp.data;
};
export const createBudgetLineItem = async (authAxios, data) => {
  const resp = await authAxios.post('budget-line-items/', data);
  return resp.data;
};
export const updateBudgetLineItem = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`budget-line-items/${id}/`, data);
  return resp.data;
};
export const deleteBudgetLineItem = async (authAxios, id) => {
  const resp = await authAxios.delete(`budget-line-items/${id}/`);
  return resp.data;
};

// --- Bill of Costs ---
export const fetchCourts = async (authAxios, params = {}) => {
  const resp = await authAxios.get('courts/', { params });
  return resp.data?.results || resp.data;
};
export const createCourt = async (authAxios, data) => {
  const resp = await authAxios.post('courts/', data);
  return resp.data;
};
export const updateCourt = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`courts/${id}/`, data);
  return resp.data;
};
export const deleteCourt = async (authAxios, id) => {
  const resp = await authAxios.delete(`courts/${id}/`);
  return resp.data;
};

// --- Judges (dropdown lookup for new case form) ---
export const fetchJudges = async (authAxios, params = {}) => {
  const resp = await authAxios.get('judges/', { params });
  return resp.data?.results || resp.data;
};
export const createJudge = async (authAxios, data) => {
  const resp = await authAxios.post('judges/', data);
  return resp.data;
};

// --- Opposing Counsel (dropdown lookup for new case form) ---
export const fetchOpposingCounsels = async (authAxios, params = {}) => {
  const resp = await authAxios.get('opposing-counsels/', { params });
  return resp.data?.results || resp.data;
};
export const createOpposingCounsel = async (authAxios, data) => {
  const resp = await authAxios.post('opposing-counsels/', data);
  return resp.data;
};

export const fetchTariffSchedules = async (authAxios, params = {}) => {
  const resp = await authAxios.get('tariff-schedules/', { params });
  return resp.data?.results || resp.data;
};
export const fetchTariffSchedule = async (authAxios, id) => {
  const resp = await authAxios.get(`tariff-schedules/${id}/`);
  return resp.data;
};
export const createTariffSchedule = async (authAxios, data) => {
  const resp = await authAxios.post('tariff-schedules/', data);
  return resp.data;
};
export const updateTariffSchedule = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`tariff-schedules/${id}/`, data);
  return resp.data;
};

export const fetchTariffItems = async (authAxios, params = {}) => {
  const resp = await authAxios.get('tariff-items/', { params });
  return resp.data?.results || resp.data;
};
export const createTariffItem = async (authAxios, data) => {
  const resp = await authAxios.post('tariff-items/', data);
  return resp.data;
};
export const updateTariffItem = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`tariff-items/${id}/`, data);
  return resp.data;
};
export const deleteTariffItem = async (authAxios, id) => {
  const resp = await authAxios.delete(`tariff-items/${id}/`);
  return resp.data;
};

export const fetchBillsOfCosts = async (authAxios, params = {}) => {
  const resp = await authAxios.get('bills-of-costs/', { params });
  return resp.data?.results || resp.data;
};
export const fetchBillOfCosts = async (authAxios, id) => {
  const resp = await authAxios.get(`bills-of-costs/${id}/`);
  return resp.data;
};
export const createBillOfCosts = async (authAxios, data) => {
  const resp = await authAxios.post('bills-of-costs/', data);
  return resp.data;
};
export const updateBillOfCosts = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`bills-of-costs/${id}/`, data);
  return resp.data;
};
export const deleteBillOfCosts = async (authAxios, id) => {
  const resp = await authAxios.delete(`bills-of-costs/${id}/`);
  return resp.data;
};
export const pullWipIntoBill = async (authAxios, id) => {
  const resp = await authAxios.post(`bills-of-costs/${id}/pull-wip/`);
  return resp.data;
};
export const recalculateBill = async (authAxios, id) => {
  const resp = await authAxios.post(`bills-of-costs/${id}/recalculate/`);
  return resp.data;
};
export const submitBillForTaxation = async (authAxios, id) => {
  const resp = await authAxios.post(`bills-of-costs/${id}/submit-taxation/`);
  return resp.data;
};
export const recordBillTaxation = async (authAxios, id, data) => {
  const resp = await authAxios.post(`bills-of-costs/${id}/record-taxation/`, data);
  return resp.data;
};
export const issueBillAllocatur = async (authAxios, id, data) => {
  const resp = await authAxios.post(`bills-of-costs/${id}/issue-allocatur/`, data);
  return resp.data;
};
export const settleBill = async (authAxios, id, data) => {
  const resp = await authAxios.post(`bills-of-costs/${id}/settle/`, data);
  return resp.data;
};
export const detectBillChanges = async (authAxios, id) => {
  const resp = await authAxios.get(`bills-of-costs/${id}/detect-changes/`);
  return resp.data;
};
export const syncBillItem = async (authAxios, billId, itemId) => {
  const resp = await authAxios.post(`bills-of-costs/${billId}/sync-item/${itemId}/`);
  return resp.data;
};
export const exportBillOfCostsPdf = async (authAxios, id) => {
  const resp = await authAxios.get(`bills-of-costs/${id}/export-pdf/`, { responseType: 'blob' });
  return resp.data;
};

// =============================================================================
// People & Performance Module
// =============================================================================

// --- Departments ---
export const listDepartments = async (authAxios, params = {}) => {
  const resp = await authAxios.get('departments/', { params });
  return resp.data?.results || resp.data;
};
export const createDepartment = async (authAxios, data) => {
  const resp = await authAxios.post('departments/', data);
  return resp.data;
};
export const updateDepartment = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`departments/${id}/`, data);
  return resp.data;
};
export const deleteDepartment = async (authAxios, id) => {
  await authAxios.delete(`departments/${id}/`);
};
export const fetchDepartmentMembers = async (authAxios, deptId) => {
  const resp = await authAxios.get(`departments/${deptId}/members/`);
  return resp.data;
};
export const fetchOrgChart = async (authAxios) => {
  const resp = await authAxios.get('departments/org_chart/');
  return resp.data;
};
export const assignDepartmentMember = async (authAxios, deptId, userId) => {
  const resp = await authAxios.post(`departments/${deptId}/assign_member/`, { user_id: userId });
  return resp.data;
};
export const removeDepartmentMember = async (authAxios, deptId, userId) => {
  const resp = await authAxios.post(`departments/${deptId}/remove_member/`, { user_id: userId });
  return resp.data;
};

// --- Employee Activities ---
export const listEmployeeActivities = async (authAxios, params = {}) => {
  const resp = await authAxios.get('employee-activities/', { params });
  return resp.data?.results || resp.data;
};
export const createEmployeeActivity = async (authAxios, data) => {
  const resp = await authAxios.post('employee-activities/', data);
  return resp.data;
};
export const updateEmployeeActivity = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`employee-activities/${id}/`, data);
  return resp.data;
};
export const deleteEmployeeActivity = async (authAxios, id) => {
  await authAxios.delete(`employee-activities/${id}/`);
};
export const fetchMyActivities = async (authAxios, params = {}) => {
  const resp = await authAxios.get('employee-activities/my_activities/', { params });
  return resp.data?.results || resp.data;
};
export const fetchActivitySummary = async (authAxios, params = {}) => {
  const resp = await authAxios.get('employee-activities/summary/', { params });
  return resp.data;
};

// --- Daily Standups ---
export const listDailyStandups = async (authAxios, params = {}) => {
  const resp = await authAxios.get('daily-standups/', { params });
  return resp.data?.results || resp.data;
};
export const createDailyStandup = async (authAxios, data) => {
  const resp = await authAxios.post('daily-standups/', data);
  return resp.data;
};
export const updateDailyStandup = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`daily-standups/${id}/`, data);
  return resp.data;
};
export const reviewDailyStandup = async (authAxios, id, data) => {
  const resp = await authAxios.post(`daily-standups/${id}/review/`, data);
  return resp.data;
};
export const deleteDailyStandup = async (authAxios, id) => {
  await authAxios.delete(`daily-standups/${id}/`);
};

// --- Review Cycles ---
export const listReviewCycles = async (authAxios, params = {}) => {
  const resp = await authAxios.get('review-cycles/', { params });
  return resp.data?.results || resp.data;
};
export const createReviewCycle = async (authAxios, data) => {
  const resp = await authAxios.post('review-cycles/', data);
  return resp.data;
};
export const updateReviewCycle = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`review-cycles/${id}/`, data);
  return resp.data;
};
export const deleteReviewCycle = async (authAxios, id) => {
  await authAxios.delete(`review-cycles/${id}/`);
};

// --- Performance Goals ---
export const listPerformanceGoals = async (authAxios, params = {}) => {
  const resp = await authAxios.get('performance-goals/', { params });
  return resp.data?.results || resp.data;
};
export const createPerformanceGoal = async (authAxios, data) => {
  const resp = await authAxios.post('performance-goals/', data);
  return resp.data;
};
export const updatePerformanceGoal = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`performance-goals/${id}/`, data);
  return resp.data;
};
export const deletePerformanceGoal = async (authAxios, id) => {
  await authAxios.delete(`performance-goals/${id}/`);
};
export const fetchMyGoals = async (authAxios, params = {}) => {
  const resp = await authAxios.get('performance-goals/my_goals/', { params });
  return resp.data?.results || resp.data;
};

// --- Performance Reviews ---
export const listPerformanceReviews = async (authAxios, params = {}) => {
  const resp = await authAxios.get('performance-reviews/', { params });
  return resp.data?.results || resp.data;
};
export const createPerformanceReview = async (authAxios, data) => {
  const resp = await authAxios.post('performance-reviews/', data);
  return resp.data;
};
export const updatePerformanceReview = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`performance-reviews/${id}/`, data);
  return resp.data;
};
export const deletePerformanceReview = async (authAxios, id) => {
  await authAxios.delete(`performance-reviews/${id}/`);
};
export const submitSelfAssessment = async (authAxios, id, data) => {
  const resp = await authAxios.post(`performance-reviews/${id}/submit_self_assessment/`, data);
  return resp.data;
};
export const submitManagerReview = async (authAxios, id, data) => {
  const resp = await authAxios.post(`performance-reviews/${id}/submit_manager_review/`, data);
  return resp.data;
};
export const completePerformanceReview = async (authAxios, id) => {
  const resp = await authAxios.post(`performance-reviews/${id}/complete/`);
  return resp.data;
};
export const acknowledgePerformanceReview = async (authAxios, id) => {
  const resp = await authAxios.post(`performance-reviews/${id}/acknowledge/`);
  return resp.data;
};

// --- Team Performance Report ---
export const fetchTeamPerformance = async (authAxios, params = {}) => {
  const resp = await authAxios.get('reports/team-performance/', { params });
  return resp.data;
};

export default api;

export const autoGeneratePayroll = async (authAxios, id) => {
  const resp = await authAxios.post(`payroll-runs/${id}/auto-generate/`);
  return resp.data;
};

export const fetchAnnualITPReport = async (authAxios, params = {}) => {
  const resp = await authAxios.get('accounting/itp-report/', { params });
  return resp.data;
};

// Bank Feed Connections
export const listBankFeedConnections = async (authAxios, params = {}) => {
  const resp = await authAxios.get('bank-feed-connections/', { params });
  return resp.data;
};

export const createBankFeedConnection = async (authAxios, data) => {
  const resp = await authAxios.post('bank-feed-connections/', data);
  return resp.data;
};

export const deleteBankFeedConnection = async (authAxios, id) => {
  const resp = await authAxios.delete('bank-feed-connections/' + id + '/');
  return resp.data;
};

export const syncBankFeed = async (authAxios, id) => {
  const resp = await authAxios.post('bank-feed-connections/' + id + '/sync/');
  return resp.data;
};

// =============================================================================
// Financial Statements & Reports
// =============================================================================

export const fetchCashFlowStatement = async (authAxios, params = {}) => {
  const resp = await authAxios.get('reports/cash-flow/', { params });
  return resp.data;
};

export const exportReport = async (authAxios, data) => {
  const resp = await authAxios.post('reports/export/', data, { responseType: 'blob' });
  return resp.data;
};

// =============================================================================
// Accounting Periods & GL Config
// =============================================================================

export const fetchAccountingPeriods = async (authAxios, params = {}) => {
  const resp = await authAxios.get('accounting-periods/', { params });
  return resp.data?.results || resp.data;
};

export const createAccountingPeriod = async (authAxios, data) => {
  const resp = await authAxios.post('accounting-periods/', data);
  return resp.data;
};

export const closeAccountingPeriod = async (authAxios, id, data = {}) => {
  const resp = await authAxios.post(`accounting-periods/${id}/close/`, data);
  return resp.data;
};

// `reason` is required by the backend — reopening a closed period unlocks it
// for backdated postings, so it is recorded in the audit log with who did it.
export const reopenAccountingPeriod = async (authAxios, id, reason) => {
  const resp = await authAxios.post(`accounting-periods/${id}/reopen/`, { reason });
  return resp.data;
};

// Aged receivables (debtors), bucketed server-side. The single source of truth
// for AR aging — see AR_AGING_BUCKETS in legal_ops/views.py. [G-3][H3]
// Firm-wide collections worklist: the overdue book with its dunning state.
// Built on the same AR aging the reports use, so the two cannot disagree. [H5]
export const fetchCollectionsWorklist = async (authAxios, params = {}) => {
  const resp = await authAxios.get('reports/collections-worklist/', { params });
  return resp.data;
};

export const fetchAgedReceivables = async (authAxios, params = {}) => {
  const resp = await authAxios.get('reports/aged-receivables/', { params });
  return resp.data;
};

export const fetchClientStatementPdf = async (authAxios, clientId, params = {}) => {
  const resp = await authAxios.get(`clients/${clientId}/statement/`, {
    params,
    responseType: 'blob',
  });
  return resp.data;
};

export const generateAccountingPeriods = async (authAxios, data) => {
  const resp = await authAxios.post('accounting-periods/generate/', data);
  return resp.data;
};

export const fetchGLConfig = async (authAxios) => {
  const resp = await authAxios.get('firm/gl-config/');
  return resp.data;
};

export const updateGLConfig = async (authAxios, data) => {
  const resp = await authAxios.patch('firm/gl-config/', data);
  return resp.data;
};

// ── Phase 4: Outlook Integration ────────────────────────────────────
export const getOutlookConnectUrl = async (authAxios) => {
  const resp = await authAxios.get('outlook/connect/');
  return resp.data; // { auth_url }
};

export const getOutlookStatus = async (authAxios) => {
  const resp = await authAxios.get('outlook/status/');
  return resp.data;
};

export const disconnectOutlook = async (authAxios) => {
  const resp = await authAxios.post('outlook/disconnect/');
  return resp.data;
};

export const composeOutlookEmail = async (authAxios, data) => {
  // data: { to: [...], cc: [...], subject, body_html, case_id? }
  const resp = await authAxios.post('outlook/compose/', data);
  return resp.data;
};

export const triggerOutlookSync = async (authAxios, type = 'email') => {
  const resp = await authAxios.post('outlook/sync/', { type });
  return resp.data;
};

export const getOutlookDiagnostics = async (authAxios) => {
  const resp = await authAxios.get('outlook/diagnostics/');
  return resp.data;
};

// Unlinked Inbox
export const fetchUnlinkedEmails = async (authAxios, params = {}) => {
  const resp = await authAxios.get('unlinked-emails/', { params });
  return resp.data;
};

export const fetchEmailDetail = async (authAxios, emailId) => {
  const resp = await authAxios.get(`outlook/email/${emailId}/`);
  return resp.data;
};

export const linkEmailToCase = async (authAxios, emailId, caseId) => {
  const resp = await authAxios.post(`unlinked-emails/${emailId}/link/`, { case_id: caseId });
  return resp.data;
};

export const dismissUnlinkedEmail = async (authAxios, emailId) => {
  const resp = await authAxios.post(`unlinked-emails/${emailId}/dismiss/`);
  return resp.data;
};

export const dismissUnlinkedEmailsBulk = async (authAxios, ids) => {
  const resp = await authAxios.post('unlinked-emails/dismiss-bulk/', { ids });
  return resp.data;
};

export const markEmailRead = async (authAxios, emailId) => {
  const resp = await authAxios.post(`unlinked-emails/${emailId}/mark-read/`);
  return resp.data;
};

export const unlinkEmailFromCase = async (authAxios, emailId) => {
  const resp = await authAxios.post(`unlinked-emails/${emailId}/unlink/`);
  return resp.data;
};

// Email Signature
export const getEmailSignature = async (authAxios) => {
  const resp = await authAxios.get('outlook/signature/');
  return resp.data;
};

export const updateEmailSignature = async (authAxios, signature) => {
  const resp = await authAxios.put('outlook/signature/', { email_signature: signature });
  return resp.data;
};

// Firm Users (for CC dropdown)
export const listFirmUsers = async (authAxios, params = {}) => {
  const resp = await authAxios.get('staff-profiles/', { params });
  return resp.data?.results || resp.data;
};

// Matter Email Rules
export const fetchMatterEmailRules = async (authAxios, params = {}) => {
  const resp = await authAxios.get('matter-email-rules/', { params });
  return resp.data;
};

export const createMatterEmailRule = async (authAxios, data) => {
  const resp = await authAxios.post('matter-email-rules/', data);
  return resp.data;
};

export const updateMatterEmailRule = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`matter-email-rules/${id}/`, data);
  return resp.data;
};

export const deleteMatterEmailRule = async (authAxios, id) => {
  const resp = await authAxios.delete(`matter-email-rules/${id}/`);
  return resp.data;
};

// --- Bill of Costs line items (manual CRUD while BoC is in DRAFT) ---
export const createBillOfCostsItem = async (authAxios, payload) => {
  const resp = await authAxios.post('bill-of-costs-items/', payload);
  return resp.data;
};

export const updateBillOfCostsItem = async (authAxios, id, payload) => {
  const resp = await authAxios.patch(`bill-of-costs-items/${id}/`, payload);
  return resp.data;
};

export const deleteBillOfCostsItem = async (authAxios, id) => {
  const resp = await authAxios.delete(`bill-of-costs-items/${id}/`);
  return resp.data;
};

// ─── Conveyancing Module ──────────────────────────────────────────────────────

// Dashboard
export const fetchConveyancingDashboard = async (authAxios) => {
  const resp = await authAxios.get('dashboard/conveyancing/');
  return resp.data;
};

// Transfer duty quick calculator (no case required)
export const calculateTransferDuty = async (authAxios, payload) => {
  const resp = await authAxios.post('transfer-duty/calculate/', payload);
  return resp.data;
};

// Properties
export const fetchProperties = async (authAxios, params = {}) => {
  return fetchAllPages(authAxios, 'properties/', params);
};

export const fetchProperty = async (authAxios, id) => {
  const resp = await authAxios.get(`properties/${id}/`);
  return resp.data;
};

export const createProperty = async (authAxios, data) => {
  const resp = await authAxios.post('properties/', data);
  return resp.data;
};

export const updateProperty = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`properties/${id}/`, data);
  return resp.data;
};

export const deleteProperty = async (authAxios, id) => {
  const resp = await authAxios.delete(`properties/${id}/`);
  return resp.data;
};

// ConveyancingDetail
export const fetchConveyancingDetail = async (authAxios, id) => {
  const resp = await authAxios.get(`conveyancing-details/${id}/`);
  return resp.data;
};

export const fetchConveyancingDetails = async (authAxios) => {
  const resp = await authAxios.get('conveyancing-details/');
  const data = resp.data?.results ?? resp.data;
  return Array.isArray(data) ? data : [];
};

export const fetchConveyancingDetailByCase = async (authAxios, caseId) => {
  const resp = await authAxios.get('conveyancing-details/', { params: { case: caseId } });
  const data = resp.data?.results ?? resp.data;
  return Array.isArray(data) ? data[0] : null;
};

export const updateConveyancingDetail = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`conveyancing-details/${id}/`, data);
  return resp.data;
};

export const calculateConveyancingFees = async (authAxios, id, payload) => {
  const resp = await authAxios.post(`conveyancing-details/${id}/calculate-fees/`, payload);
  return resp.data;
};

export const fetchConveyancingCostLetterPdf = async (authAxios, id) => {
  const resp = await authAxios.get(`conveyancing-details/${id}/cost-letter-pdf/`, { responseType: 'blob' });
  return resp.data;
};

export const fetchConveyancingFeeSnapshotPdf = async (authAxios, id) => {
  const resp = await authAxios.get(`conveyancing-details/${id}/fee-snapshot-pdf/`, { responseType: 'blob' });
  return resp.data;
};

export const fetchConveyancingFiaStatus = async (authAxios, id) => {
  const resp = await authAxios.get(`conveyancing-details/${id}/fia-status/`);
  return resp.data;
};

export const createConveyancingInvoice = async (authAxios, id) => {
  const resp = await authAxios.post(`conveyancing-details/${id}/create-invoice/`);
  return resp.data;
};

export const fetchConveyancingAuditLog = async (authAxios, id) => {
  const resp = await authAxios.get(`conveyancing-details/${id}/audit-log/`);
  return resp.data;
};

// ConveyancingParties
export const fetchConveyancingParties = async (authAxios, detailId) => {
  const resp = await authAxios.get('conveyancing-parties/', { params: { conveyancing_detail: detailId } });
  return resp.data?.results ?? resp.data;
};

export const createConveyancingParty = async (authAxios, data) => {
  const resp = await authAxios.post('conveyancing-parties/', data);
  return resp.data;
};

export const updateConveyancingParty = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`conveyancing-parties/${id}/`, data);
  return resp.data;
};

export const deleteConveyancingParty = async (authAxios, id) => {
  const resp = await authAxios.delete(`conveyancing-parties/${id}/`);
  return resp.data;
};

// DeedInstruments
export const fetchDeedInstruments = async (authAxios, detailId) => {
  const resp = await authAxios.get('deed-instruments/', { params: { conveyancing_detail: detailId } });
  return resp.data?.results ?? resp.data;
};

export const createDeedInstrument = async (authAxios, data) => {
  const resp = await authAxios.post('deed-instruments/', data);
  return resp.data;
};

export const updateDeedInstrument = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`deed-instruments/${id}/`, data);
  return resp.data;
};

export const deleteDeedInstrument = async (authAxios, id) => {
  const resp = await authAxios.delete(`deed-instruments/${id}/`);
  return resp.data;
};

// ExaminerNotes
export const fetchExaminerNotes = async (authAxios, instrumentId) => {
  const resp = await authAxios.get('examiner-notes/', { params: { instrument: instrumentId } });
  return resp.data?.results ?? resp.data;
};

export const createExaminerNote = async (authAxios, data) => {
  const resp = await authAxios.post('examiner-notes/', data);
  return resp.data;
};

export const updateExaminerNote = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`examiner-notes/${id}/`, data);
  return resp.data;
};

// ConveyancingBatches
export const fetchConveyancingBatches = async (authAxios, params = {}) => {
  const resp = await authAxios.get('conveyancing-batches/', { params });
  return resp.data?.results ?? resp.data;
};

export const createConveyancingBatch = async (authAxios, data) => {
  const resp = await authAxios.post('conveyancing-batches/', data);
  return resp.data;
};

export const updateConveyancingBatch = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`conveyancing-batches/${id}/`, data);
  return resp.data;
};

export const deleteConveyancingBatch = async (authAxios, id) => {
  const resp = await authAxios.delete(`conveyancing-batches/${id}/`);
  return resp.data;
};

export const fetchBatchEligibleMatters = async (authAxios, batchId) => {
  const resp = await authAxios.get('conveyancing-batches/eligible-matters/', {
    params: batchId ? { batch: batchId } : {},
  });
  return resp.data;
};

export const addBatchMember = async (authAxios, batchId, payload) => {
  const resp = await authAxios.post(`conveyancing-batches/${batchId}/add-member/`, payload);
  return resp.data;
};

export const removeBatchMember = async (authAxios, batchId, memberId) => {
  const resp = await authAxios.post(`conveyancing-batches/${batchId}/remove-member/`, { member_id: memberId });
  return resp.data;
};

export const setBatchMemberReady = async (authAxios, batchId, memberId, isReady) => {
  const resp = await authAxios.post(`conveyancing-batches/${batchId}/set-member-ready/`, {
    member_id: memberId,
    is_ready: isReady,
  });
  return resp.data;
};

// BankInstructions
export const fetchBankInstructions = async (authAxios, params = {}) => {
  return fetchAllPages(authAxios, 'bank-instructions/', params);
};

export const fetchBankInstruction = async (authAxios, id) => {
  const resp = await authAxios.get(`bank-instructions/${id}/`);
  return resp.data;
};

export const createBankInstruction = async (authAxios, data) => {
  const resp = await authAxios.post('bank-instructions/', data);
  return resp.data;
};

export const updateBankInstruction = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`bank-instructions/${id}/`, data);
  return resp.data;
};

export const recordBankInstructionTrustDeposit = async (authAxios, id, amount, trustAccountId) => {
  const resp = await authAxios.post(`bank-instructions/${id}/record-trust-deposit/`, {
    amount,
    ...(trustAccountId ? { trust_account: trustAccountId } : {}),
  });
  return resp.data;
};

// Conveyancing Reports (supports optional filters: date_from, date_to, conveyancer_id, stage)
export const fetchConveyancingReports = async (authAxios, filters = {}) => {
  const params = {};
  if (filters.date_from) params.date_from = filters.date_from;
  if (filters.date_to) params.date_to = filters.date_to;
  if (filters.conveyancer_id) params.conveyancer_id = filters.conveyancer_id;
  if (filters.stage) params.stage = filters.stage;
  const resp = await authAxios.get('reports/conveyancing/', { params });
  return resp.data;
};

// BURS TD1 Transfer Duty Declaration PDF
export const downloadBursTD1Pdf = async (authAxios, caseId) => {
  const resp = await authAxios.get(`cases/${caseId}/burs-td1-pdf/`, { responseType: 'blob' });
  return resp;
};

// Closing Statement PDF
export const downloadClosingStatement = async (authAxios, detailId) => {
  const resp = await authAxios.get(`conveyancing-details/${detailId}/closing-statement-pdf/`, {
    responseType: 'blob',
  });
  return resp;
};

// Trust Requisitions
export const fetchTrustRequisitions = async (authAxios, caseId) => {
  const resp = await authAxios.get('trust-requisitions/', { params: { case: caseId } });
  return resp.data?.results ?? resp.data;
};

export const createTrustRequisition = async (authAxios, data) => {
  const resp = await authAxios.post('trust-requisitions/', data);
  return resp.data;
};

export const updateTrustRequisition = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`trust-requisitions/${id}/`, data);
  return resp.data;
};

export const authoriseTrustRequisition = async (authAxios, id) => {
  const resp = await authAxios.post(`trust-requisitions/${id}/authorise/`);
  return resp.data;
};

export const payTrustRequisitionNow = async (authAxios, id, trustAccountId) => {
  const resp = await authAxios.post(`trust-requisitions/${id}/pay-now/`, {
    ...(trustAccountId ? { trust_account: trustAccountId } : {}),
  });
  return resp.data;
};

export const downloadTrustRequisitionPdf = async (authAxios, id) => {
  const resp = await authAxios.get(`trust-requisitions/${id}/pdf/`, {
    responseType: 'blob',
  });
  return resp;
};

// Per-Matter Trust Ledger
export const fetchCaseTrustLedger = async (authAxios, caseId) => {
  const resp = await authAxios.get(`cases/${caseId}/trust-ledger/`);
  return resp.data;
};

export const downloadCaseTrustLedgerPdf = async (authAxios, caseId) => {
  const resp = await authAxios.get(`cases/${caseId}/trust-ledger-pdf/`, {
    responseType: 'blob',
  });
  return resp;
};

// ── Two-Factor Authentication (Sprint B3) ───────────────────────────
// Note: verify2FA uses the base api (no auth token yet) since the user
// hasn't completed login when calling it.

export const verify2FA = async (partialToken, totpCode) => {
  const resp = await api.post('auth/2fa/verify/', {
    partial_token: partialToken,
    totp_code: totpCode,
  });
  return resp.data;
};

export const setup2FA = async (authAxios) => {
  const resp = await authAxios.post('auth/2fa/setup/');
  return resp.data;
};

export const confirm2FA = async (authAxios, totpCode) => {
  const resp = await authAxios.post('auth/2fa/confirm/', { totp_code: totpCode });
  return resp.data;
};

export const disable2FA = async (authAxios, password) => {
  const resp = await authAxios.post('auth/2fa/disable/', { password });
  return resp.data;
};

export const get2FAStatus = async (authAxios) => {
  const resp = await authAxios.get('auth/2fa/status/');
  return resp.data;
};

// ─── Branded-PDF infrastructure (verify URL, send-by-email, debtor history) ───

/**
 * Convert a Blob (typically a PDF the user has just downloaded) to base64
 * for use with sendPdfByEmail.
 */
export const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const result = String(reader.result || '');
    // strip the "data:application/pdf;base64," prefix
    const comma = result.indexOf(',');
    resolve(comma >= 0 ? result.slice(comma + 1) : result);
  };
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

/**
 * Generic email-the-PDF endpoint. Takes a PDF Blob (or base64 string) plus
 * recipients/subject/body and asks the server to email it as an attachment.
 *
 *   await sendPdfByEmail(authAxios, {
 *     pdf: pdfBlob,
 *     filename: 'INV-2026-05-00001.pdf',
 *     recipients: ['client@example.com'],
 *     subject: 'Invoice INV-2026-05-00001',
 *     body: 'Dear client,...',
 *     document_kind: 'INVOICE', object_id: '123',
 *   });
 */
export const sendPdfByEmail = async (authAxios, {
  pdf,
  filename,
  recipients,
  subject,
  body,
  cc = [],
  bcc = [],
  reply_to,
  document_kind = '',
  object_id = '',
  case_id = null,
  client_id = null,
}) => {
  const pdf_base64 = pdf instanceof Blob ? await blobToBase64(pdf) : pdf;
  const resp = await authAxios.post('send-pdf-by-email/', {
    pdf_base64, filename, recipients, subject, body,
    cc, bcc, reply_to, document_kind, object_id, case_id, client_id,
  });
  return resp.data;
};

/**
 * Public document-verify endpoint (target of the QR code on every branded PDF).
 * Used by the firm's own /v/:token route — no auth header required.
 */
export const verifyDocumentToken = async (tokenId) => {
  const resp = await api.get(`v/${tokenId}/`);
  return resp.data;
};

/**
 * Debtor payment history statement (debt recovery module).
 */
export const downloadDebtorPaymentHistoryPdf = async (authAxios, debtorId) => {
  const resp = await authAxios.get(`debt-recovery-debtors/${debtorId}/payment-history-pdf/`, {
    responseType: 'blob',
  });
  return resp.data;
};

/**
 * Semantic (vector) search across the authenticated user's firm documents.
 * Calls the firm-scoped pgvector endpoint backed by Azure OpenAI embeddings.
 *
 * @param {Object}  args
 * @param {string}  args.query            Free-text query.
 * @param {number} [args.top_k=10]        Max results (server caps at 50).
 * @param {string} [args.document_type]   Optional Document.document_type filter.
 * @param {number} [args.case_id]         Optional Case ID filter.
 */
export const semanticSearchDocuments = async (
  authAxios,
  { query, top_k = 10, document_type = null, case_id = null } = {},
) => {
  const payload = { query, top_k };
  if (document_type) payload.document_type = document_type;
  if (case_id) payload.case_id = case_id;
  const resp = await authAxios.post('documents/semantic-search/', payload);
  return resp.data;  // { results: [...], query, total }
};
